import { useCallback, useEffect, useRef, useState } from 'react'

import {
  closestCenter,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core'
import type {
  Active,
  CollisionDetection,
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  Over,
  UniqueIdentifier,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'

import { useCustomSensors } from '~publish/hooks/useCustomSensors'

import { UNASSIGNED_GROUP_ID } from '../helpers/helpers'

type Items = Record<UniqueIdentifier, UniqueIdentifier[]>
type BoardHandlers = {
  activeId: UniqueIdentifier | null
  items: Items
  columns: UniqueIdentifier[]
  lockOverlayId: UniqueIdentifier | null
  contextProps: {
    sensors: ReturnType<typeof useCustomSensors>
    collisionDetection: CollisionDetection
    onDragStart: (event: DragStartEvent) => void
    onDragEnd: (event: DragEndEvent) => void
    onDragOver: (event: DragOverEvent) => void
    onDragCancel: (event?: DragCancelEvent) => void
  }
}

type ItemMovedCallback = ({
  groupId,
  ideaId,
  placeAfterId,
}: {
  groupId?: string
  ideaId: string
  placeAfterId?: string
}) => Promise<void>

type ColumnMovedCallback = ({
  id,
  placeAfterId,
}: {
  id: string
  placeAfterId?: string
}) => Promise<void>

function findContainer(
  id: UniqueIdentifier,
  boardItems: Items,
): UniqueIdentifier | undefined {
  if (Object.hasOwn(boardItems, id)) return id

  return Object.keys(boardItems).find((key) => boardItems[key].includes(id))
}

/**
 * Custom hook to setup a "Kanban" like board with drag and drop functionality.
 *
 * @param {Items} initialItems - Latest server state for the board items.
 * @param {Function} onItemMoved - Callback to be called when an item is moved.
 * @returns {BoardHandlers} Hook state and handlers for drag and drop events.
 */
export function useDndBoard(
  initialItems: Items,
  onItemMoved: ItemMovedCallback,
  onColumnMoved: ColumnMovedCallback,
  isLocked: (id: UniqueIdentifier) => boolean,
): BoardHandlers {
  const [items, setItems] = useState<Items>(initialItems ?? {})
  const [columns, setColumns] = useState<UniqueIdentifier[]>(
    Object.keys(initialItems ?? {}),
  )
  const [previousInitialItems, setPreviousInitialItems] = useState(initialItems)
  const [activeItemId, setActiveItemId] = useState<UniqueIdentifier | null>(
    null,
  )
  const [clonedItems, setClonedItems] = useState<Items | null>(null)

  const recentlyMovedToNewContainer = useRef(false)
  const lastOverId = useRef<UniqueIdentifier | null>(null)

  const sensors = useCustomSensors()
  const [lockOverlayId, setLockOverlayId] = useState<UniqueIdentifier | null>(
    null,
  )

  if (initialItems !== previousInitialItems) {
    setPreviousInitialItems(initialItems)
    setItems(initialItems)
    setColumns(Object.keys(initialItems))
  }

  useEffect(
    function resetRecentlyMovedToNewContainer() {
      const animationFrame = requestAnimationFrame(() => {
        recentlyMovedToNewContainer.current = false
      })

      return (): void => cancelAnimationFrame(animationFrame)
    },
    [items],
  )

  /**
   * Custom collision detection strategy optimized for multiple containers.
   * When dragging containers, resolve using rectIntersection.
   *
   * Steps:
   * 1. Prioritize droppable containers intersecting with the pointer.
   * 2. If none, fallback to containers intersecting with the draggable.
   * 3. Use the last matched intersection if no current intersections are found.
   */
  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      if (args.active?.data?.current?.type === 'container') {
        return rectIntersection(args)
      }

      // First, attempt to find droppables intersecting with the pointer.
      let intersections = pointerWithin(args)
      if (intersections.length === 0) {
        // If no pointer intersections, check for rectangle intersections.
        intersections = rectIntersection(args)
      }

      let overId = getFirstCollision(intersections, 'id')

      // Adjust overId for containers with items, aiming for closest center within.
      if (overId && overId in items && items[overId].length > 0) {
        const containerItems = items[overId]
        overId = closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (container) =>
              container.id !== overId && containerItems.includes(container.id),
          ),
        })[0]?.id
      }

      // Cache the last overId, or update it based on recent movements.
      if (overId) {
        lastOverId.current = overId
      } else if (recentlyMovedToNewContainer.current && activeItemId) {
        // When a draggable item moves to a new container, the layout may shift
        // and the `overId` may become `null`. We manually set the cached `lastOverId`
        // to the id of the draggable item that was moved to the new container, otherwise
        // the previous `overId` will be returned which can cause items to incorrectly shift positions
        lastOverId.current = activeItemId
      }

      // Return the current or last known overId, if any.
      return overId
        ? [{ id: overId }]
        : lastOverId.current
          ? [{ id: lastOverId.current }]
          : []
    },
    [activeItemId, items],
  )

  function onDragStart({ active }: DragStartEvent): void {
    setActiveItemId(active.id)
    setClonedItems(items)
  }

  function onDragOver({ active, over }: DragOverEvent): void {
    const overId = over?.id

    if (overId == null || active.data?.current?.type === 'container') {
      return
    }

    const overContainer = findContainer(overId, items)
    const activeContainer = findContainer(active.id, items)
    const previousContainer = findContainer(active.id, clonedItems ?? items)

    setLockOverlayId(
      overContainer && isLocked(overContainer) ? overContainer : null,
    )

    if (
      !overContainer ||
      !activeContainer ||
      !previousContainer ||
      activeContainer === overContainer ||
      (isLocked(overContainer) && overContainer !== previousContainer)
    ) {
      setLockOverlayId(null)
      return
    }

    // Use a setTimeout to avoid 'Maximum update depth exceeded' error
    // see more: https://github.com/clauderic/dnd-kit/issues/496#issuecomment-1789098099
    setTimeout(
      () =>
        setItems((currentItems) => {
          const activeItem = currentItems[activeContainer].find(
            (item) => item === active.id,
          )
          const overItemIndex = currentItems[overContainer].indexOf(overId)

          if (activeItem === undefined) return currentItems

          let newIndex: number = overItemIndex

          // Calculate new index based on position relative to over item
          if (active?.rect?.current?.translated) {
            const isBelowOverItem =
              over &&
              active.rect.current.translated.top >
                over.rect.top + over.rect.height
            newIndex += isBelowOverItem ? 1 : 0
          } else {
            // Default to adding to the end if no translated position is available
            newIndex = currentItems[overContainer].length
          }

          recentlyMovedToNewContainer.current = true

          return {
            ...currentItems,
            // Remove the active item from the active container
            [activeContainer]: currentItems[activeContainer].filter(
              (item) => item !== active.id,
            ),
            // Add the active item to the over container at the new index
            [overContainer]: [
              ...currentItems[overContainer].slice(0, newIndex),
              activeItem,
              ...currentItems[overContainer].slice(
                newIndex,
                currentItems[overContainer].length,
              ),
            ],
          }
        }),
      0,
    )
  }

  function onDragEnd({ active, over }: DragEndEvent): void {
    setLockOverlayId(null)

    if (
      active?.data?.current?.type === 'container' &&
      over?.data?.current?.type === 'container'
    ) {
      handleOnContainerDragEnd(active, over)
    } else if (active?.data?.current?.type === 'item') {
      handleOnItemDragEnd(active, over)
    }

    setActiveItemId(null)
  }

  function onDragCancel(): void {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems)
    }

    setActiveItemId(null)
    setClonedItems(null)
    setLockOverlayId(null)
  }

  return {
    activeId: activeItemId,
    items,
    columns,
    lockOverlayId,
    contextProps: {
      collisionDetection,
      sensors,
      onDragStart,
      onDragOver,
      onDragEnd,
      onDragCancel,
    },
  }

  function handleOnContainerDragEnd(active: Active, over: Over): void {
    if (isLocked(active.id) || isLocked(over.id)) return

    const newColumns = arrayMove(
      columns,
      columns.indexOf(active.id),
      columns.indexOf(over.id),
    )
    setColumns(newColumns)
    const activeIndex = newColumns.indexOf(active.id)
    const id = typeof active.id === 'string' ? active.id : undefined
    const placeAfterId = newColumns[activeIndex - 1]
    if (id === undefined) return

    onColumnMoved({
      id,
      placeAfterId: typeof placeAfterId === 'string' ? placeAfterId : undefined,
    }).catch(() => setColumns(columns))
  }

  function handleOnItemDragEnd(active: Active, over: Over | null): void {
    const activeContainer = findContainer(active.id, items)
    const overItemId = over?.id

    if (!activeContainer || overItemId == null) return

    const overContainer = findContainer(overItemId, items)

    if (!overContainer || isLocked(overContainer)) {
      // If the over container is locked, do not allow the item to be moved
      // to that container and reset the items to their original state.
      setItems(clonedItems ?? items)
      return
    }

    // onDragEnd the active item should be already on the same container as the
    // over item, this happened because of the onDragOver event. So we are only
    // "sorting" on the same container.
    sortItemsWithinContainer(activeContainer, active, overItemId)
  }

  function sortItemsWithinContainer(
    activeContainer: UniqueIdentifier,
    active: Active,
    overItemId: UniqueIdentifier,
  ): void {
    const activeItemIndex = items[activeContainer].indexOf(active.id)
    const overItemIndex = items[activeContainer].indexOf(overItemId)

    setItems((currentItems) => {
      const updatedItems = {
        ...currentItems,
        [activeContainer]: arrayMove(
          currentItems[activeContainer],
          activeItemIndex,
          overItemIndex,
        ),
      }

      const currentIndex = updatedItems[activeContainer].indexOf(active.id)
      const placeAfterId = updatedItems[activeContainer][currentIndex - 1]

      const previousContainer = findContainer(active.id, clonedItems ?? items)
      if (!previousContainer) return updatedItems
      const previousIndex = (clonedItems ?? items)[previousContainer].indexOf(
        active.id,
      )

      // Only call onItemMoved if the item has moved to a new container or
      // if the item has moved within the same container but to a different position
      if (
        activeContainer !== previousContainer ||
        previousIndex !== currentIndex
      ) {
        onItemMoved({
          groupId:
            activeContainer !== UNASSIGNED_GROUP_ID &&
            typeof activeContainer === 'string'
              ? activeContainer
              : undefined,
          ideaId: typeof active.id === 'string' ? active.id : String(active.id),
          placeAfterId:
            typeof placeAfterId === 'string' ? placeAfterId : undefined,
        })
      }

      return updatedItems
    })
  }
}
